import React from "react";
import { Table, Tag, Dropdown, Progress } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { translations } from "../../utils/Translation/staticTranslation";
import DiscountFilters from "./DiscountFilters";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "./Discounts.css";

dayjs.extend(utc);

const DiscountsTableWithFilters = ({
  data,
  loading = false,
  isReadOnly = false,
  canDelete: canDeleteInModule = true,
  searchValue = "",
  statusFilter = "All Status",
  planTypeFilter = "All Plan Types",
  pagination = { current: 1, pageSize: 10, total: 0 },
  onSearchChange,
  onStatusFilterChange,
  onPlanTypeFilterChange,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onCreateDiscount,
}) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.discountsPage;
  const isRTL = lang === "ar";

  const getStatusTag = (status) => {
    const normalizedStatus = (status || "").toLowerCase();

    const statusMap = {
      active: "Active",
      expired: "Expired",
      upcoming: "Upcoming",
    };

    const displayStatus = statusMap[normalizedStatus] || status || "Active";

    // Get translated status text
    const getTranslatedStatus = (statusKey) => {
      const statusTranslations = {
        Active: t?.filters?.status?.active || "Active",
        Expired: t?.filters?.status?.expired || "Expired",
        Upcoming: t?.filters?.status?.upcoming || "Upcoming",
      };
      return statusTranslations[statusKey] || statusKey;
    };

    const statusConfig = {
      Active: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
      Expired: { color: "#667085", bg: "#F2F4F7", border: "#E4E7EC" },
      Upcoming: { color: "#155EEF", bg: "#EFF8FF", border: "#B2DDFF" },
    };

    const config = statusConfig[displayStatus] || statusConfig.Active;

    return (
      <Tag
        className="discount-status-tag"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.border}`,
        }}
      >
        {getTranslatedStatus(displayStatus)}
      </Tag>
    );
  };

  const getTypeValueDisplay = (type, value) => {
    const normalizedType =
      type?.toString().toLowerCase().replace(/\s+/g, "_") || "";
    if (normalizedType === "percentage") {
      const typeLabel = isRTL
        ? t?.filters?.discount_types?.percentage || "نسبة مئوية"
        : t?.filters?.discount_types?.percentage || "Percentage";
      return { value: `${value}% off`, type: typeLabel };
    } else if (normalizedType === "fixed") {
      const typeLabel = isRTL
        ? t?.filters?.discount_types?.fixed || "مبلغ ثابت"
        : t?.filters?.discount_types?.fixed || "Fixed";
      return { value: `SAR ${value} off`, type: typeLabel };
    } else if (normalizedType === "trial_extension") {
      return { value: `+${value} days`, type: "Trial extension" };
    } else if (normalizedType === "plan_extension") {
      const monthsLabel = Number(value) === 1 ? "month" : "months";
      return { value: `${value} ${monthsLabel}`, type: "Plan Extension" };
    }
    return { value: `${value}`, type: type };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Backend now sends times with timezone offsets (e.g., +05:00)
    // Parse normally - dayjs will handle the timezone offset correctly
    const date = dayjs(dateString);
    return date.format("YYYY-MM-DD HH:mm");
  };

  const columns = [
    {
      title: t?.table?.discount_code || "Discount Code",
      dataIndex: "discountCode",
      key: "discountCode",
      width: 200,
      render: (text, record) => (
        <div>
          <div className="discount-code-cell">{text}</div>
          <div className="discount-description-cell">
            {isRTL && record.descriptionArabic
              ? record.descriptionArabic
              : record.description || ""}
          </div>
        </div>
      ),
    },
    {
      title: t?.table?.type_value || "Type & Value",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (type, record) => {
        const display = getTypeValueDisplay(type, record.value);
        return (
          <div className="discount-type-value-cell">
            <div style={{ color: "#101828" }}>{display.value}</div>
            <div
              style={{
                fontSize: "14px",
                color: "#667085",
                marginTop: "2px",
                fontWeight: "400",
              }}
            >
              {display.type}
            </div>
          </div>
        );
      },
    },
    {
      title: t?.table?.validity_period || "Validity Period",
      dataIndex: "validityPeriod",
      key: "validityPeriod",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="discount-validity-start" style={{ color: "#101828" }}>
            {formatDate(record.startDate)}
          </div>
          <div className="discount-validity-end" style={{ color: "#8C8C8C" }}>
            {formatDate(record.endDate)}
          </div>
        </div>
      ),
    },
    {
      title: t?.table?.target_plans || "Target Plans",
      dataIndex: "targetPlans",
      key: "targetPlans",
      width: 150,
      render: (plans, record) => {
        if (!plans || (Array.isArray(plans) && plans.length === 0))
          return <span>—</span>;
        const planArray = Array.isArray(plans) ? plans : [plans];
        const plansArabic = record.targetPlansArabic || [];
        const plansArabicArray = Array.isArray(plansArabic) ? plansArabic : [];

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {planArray.map((plan, index) => {
              const displayPlan =
                isRTL && plansArabicArray[index]
                  ? plansArabicArray[index]
                  : plan;
              return (
                <Tag key={index} className="discount-plan-tag">
                  {displayPlan}
                </Tag>
              );
            })}
          </div>
        );
      },
    },
    {
      title: t?.table?.audience || "Audience",
      dataIndex: "audience",
      key: "audience",
      width: 150,
      render: (text, record) => {
        if (!text || text === "—") return <span>—</span>;
        // Use Arabic if available and language is Arabic
        const textStr =
          isRTL && record.audienceArabic
            ? record.audienceArabic.trim()
            : text.trim();
        const audianceTag =
          isRTL && record.audianceTagArabic
            ? record.audianceTagArabic
            : record.audianceTag || "";

        return (
          <div
            className="discount-audience-cell"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              flexWrap: "wrap",
            }}
          >
            <span>{textStr}</span>
            {audianceTag && (
              <Tag
                className="discount-plan-tag"
                style={{
                  color: "#344054",
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E4E7EC",
                  borderRadius: "6px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {audianceTag}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t?.table?.usage || "Usage",
      dataIndex: "usage",
      key: "usage",
      width: 150,
      render: (_, record) => {
        const used = record.used || 0;
        const total = record.total; // Can be null
        const hasMaxRedemptions = total !== null && total !== undefined;
        const percentage =
          hasMaxRedemptions && total > 0 ? Math.round((used / total) * 100) : 0;

        return (
          <div>
            <div style={{ marginBottom: "2px" }}>
              {hasMaxRedemptions ? `${used} / ${total}` : used}
            </div>
            {hasMaxRedemptions && (
              <Progress
                percent={percentage}
                showInfo={false}
                strokeColor="#007AFF"
                trailColor="#E5E7EB"
                size="small"
              />
            )}
          </div>
        );
      },
    },
    {
      title: t?.table?.status || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: t?.table?.actions || "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/arrow-up-right.svg"
                  alt="View"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.view || "View"}
              </span>
            ),
            onClick: () => onView && onView(record),
          },
        ];

        const normalizedStatus = (record.status || "").toLowerCase();
        const isExpired = normalizedStatus === "expired";
        const isActive = normalizedStatus === "active";
        const canEdit = true;
        const canDelete = !isExpired && !isActive; // Only upcoming discounts can be deleted

        if (!isReadOnly && canEdit) {
          menuItems.push({
            key: "edit",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/Button-edit.svg"
                  alt="Edit"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.edit || "Edit"}
              </span>
            ),
            onClick: () => onEdit && onEdit(record),
          });
        }

        if (!isReadOnly && canDeleteInModule && canDelete) {
          menuItems.push({
            key: "delete",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/delete-icon.svg"
                  alt="Delete"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.delete || "Delete"}
              </span>
            ),
            onClick: () => onDelete && onDelete(record),
          });
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <MoreOutlined className="discount-actions-icon" />
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (paginationInfo) => {
    if (onPageChange) {
      onPageChange(paginationInfo.current);
    }
  };

  return (
    <div className="discounts-table-container discounts-table-wrapper">
      <DiscountFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        planTypeFilter={planTypeFilter}
        onPlanTypeFilterChange={onPlanTypeFilterChange}
        onCreateDiscount={onCreateDiscount}
        isReadOnly={isReadOnly}
      />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showSizeChanger: false,
          showLessItems: false,
          position: ["bottomRight"],
          style: {
            marginTop: "16px",
            marginBottom: "16px",
            textAlign: "right",
          },
          itemRender: (page, type, originalElement) => {
            const totalPages = Math.ceil(
              pagination.total / pagination.pageSize
            );

            if (type === "prev") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      if (pagination.current < totalPages) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current + 1 });
                      }
                    } else {
                      if (pagination.current > 1) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current - 1 });
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current >= totalPages
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current === 1
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current >= totalPages
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current === 1
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current >= totalPages
                        ? "none"
                        : "auto"
                      : pagination.current === 1
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      {t?.pagination?.previous || "Previous"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Previous"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Previous"
                        style={{ width: "16px", height: "16px" }}
                      />
                      {t?.pagination?.previous || "Previous"}
                    </>
                  )}
                </a>
              );
            }
            if (type === "next") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      if (pagination.current > 1) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current - 1 });
                      }
                    } else {
                      if (pagination.current < totalPages) {
                        e.preventDefault();
                        handleTableChange({ current: pagination.current + 1 });
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current === 1
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current >= totalPages
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current === 1
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current >= totalPages
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current === 1
                        ? "none"
                        : "auto"
                      : pagination.current >= totalPages
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Next"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                      {t?.pagination?.next || "Next"}
                    </>
                  ) : (
                    <>
                      {t?.pagination?.next || "Next"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Next"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </>
                  )}
                </a>
              );
            }
            if (type === "page") {
              const isActive = page === pagination.current;
              return (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleTableChange({ current: page });
                  }}
                  style={{
                    color: "#18181B",
                    backgroundColor: isActive ? "#F9FAFB" : "transparent",
                    padding: isActive ? "8px 14px" : "8px 14px",
                    borderRadius: isActive ? "8px" : "8px",
                    fontSize: "14px",
                    fontWeight: 400,
                    border: isActive
                      ? "1px solid #D0D5DD"
                      : "1px solid transparent",
                    textDecoration: "none",
                    display: "inline-block",
                    textAlign: "center",
                    cursor: "pointer",
                    minWidth: "36px",
                  }}
                >
                  {page}
                </a>
              );
            }
            if (type === "jump-prev" || type === "jump-next") {
              let targetPage;
              if (type === "jump-prev") {
                targetPage = Math.max(1, pagination.current - 5);
              } else {
                targetPage = Math.min(totalPages, pagination.current + 5);
              }

              return (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleTableChange({ current: targetPage });
                  }}
                  style={{
                    color: "#18181B",
                    fontSize: "14px",
                    border: "none",
                    background: "transparent",
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "inline-block",
                    textAlign: "center",
                    minWidth: "36px",
                  }}
                >
                  ...
                </a>
              );
            }
            return originalElement;
          },
          onChange: (page, pageSize) => {
            handleTableChange({ current: page, pageSize });
          },
        }}
        rowKey="id"
      />
    </div>
  );
};

export default DiscountsTableWithFilters;
